import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { apiBaseUrl } from "../../constants/urls";
import { getToken } from "../../auth/AuthToken";
import { toast } from "react-toastify";

// Async Thunks
export const fetchResources = createAsyncThunk(
  'resources/fetchResources',
  async (_, { rejectWithValue, getState }) => {
    const bearerToken = getToken();
    const selectedAccount = getState()?.user?.selectedAccount;
    const url = selectedAccount
      ? `${apiBaseUrl}/resources?account_id=${selectedAccount}`
      : `${apiBaseUrl}/resources`;

    try {
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "An error occurred");
    }
  }
);

export const fetchResource = createAsyncThunk(
  'resources/fetchResource',
  async ({ id }, { rejectWithValue, getState }) => {
    const bearerToken = getToken();
    const selectedAccount = getState()?.user?.selectedAccount;
    const url = selectedAccount
      ? `${apiBaseUrl}/resources/${id}?account_id=${selectedAccount}`
      : `${apiBaseUrl}/resources/${id}`;
    try {
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const createResource = createAsyncThunk(
  'resources/createResource',
  async ({ resourceName, resourceDescription, files, externalFiles }, { rejectWithValue, getState }) => {
    const bearerToken = getToken();
    const selectedAccount = getState()?.user?.selectedAccount;
    const url = selectedAccount
      ? `${apiBaseUrl}/resources?account_id=${selectedAccount}`
      : `${apiBaseUrl}/resources`;
    
    const formData = new FormData();
    formData.append('resource[name]', resourceName);
    formData.append('resource[description]', resourceDescription || '');
    
    if (files) {
      files.forEach(file => {
        formData.append('resource[files][]', file);
      });
    }

    if (externalFiles) {
      formData.append('resource[external_files]', JSON.stringify(externalFiles));
    }

    try {
      const response = await axios.post(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      toast.success("Resource created successfully");
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateResource = createAsyncThunk(
  'resources/updateResource',
  async ({ id, resourceName, resourceDescription, order_resource_file_ids }, { rejectWithValue, getState }) => {
    const bearerToken = getToken();
    const selectedAccount = getState()?.user?.selectedAccount;
    const url = selectedAccount
      ? `${apiBaseUrl}/resources/${id}?account_id=${selectedAccount}`
      : `${apiBaseUrl}/resources/${id}`;
    const resource = {
      resource: {
        name: resourceName,
        description: resourceDescription,
        order_resource_file_ids: order_resource_file_ids, // Include reordered file IDs
      },
    };
    try {
      const response = await axios.patch(url, resource, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      toast.success("Resource updated successfully");
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteResource = createAsyncThunk(
  'resources/deleteResource',
  async ({ id }, { rejectWithValue, getState }) => {
    const bearerToken = getToken();
    const selectedAccount = getState()?.user?.selectedAccount;
    const url = selectedAccount
      ? `${apiBaseUrl}/resources/${id}?account_id=${selectedAccount}`
      : `${apiBaseUrl}/resources/${id}`;
    try {
      const response = await axios.delete(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      toast.success("Resource removed successfully");
      return id; // Return the ID of the deleted resource
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const uploadFiles = createAsyncThunk(
  'resources/uploadFiles',
  async ({ id, formData, fileType }, { rejectWithValue, getState }) => {
    const bearerToken = getToken();
    const selectedAccount = getState()?.user?.selectedAccount;
    const url = selectedAccount
      ? `${apiBaseUrl}/resources/${id}/attach_file?account_id=${selectedAccount}`
      : `${apiBaseUrl}/resources/${id}/attach_file`;

    try {
      const response = await axios.post(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      toast.success(`${fileType === 'file' ? 'File' : 'External URL'} added successfully`);
      return { resourceId: id, files: response.data.files };
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const removeFile = createAsyncThunk(
  'resources/removeFile',
  async ({ resourceId, fileId }, { rejectWithValue, getState }) => {
    const bearerToken = getToken();
    const selectedAccount = getState()?.user?.selectedAccount;
    const url = selectedAccount
      ? `${apiBaseUrl}/resources/${resourceId}/remove_file/${fileId}?account_id=${selectedAccount}`
      : `${apiBaseUrl}/resources/${resourceId}/remove_file/${fileId}`;

    try {
      const response = await axios.delete(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      toast.success("File deleted successfully");
      return { resourceId, fileId }; // Return resource ID and file ID
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Slice
const resourcesSlice = createSlice({
  name: 'resources',
  initialState: {
    resources: [],
    resource: null,
    meta: {},
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch Resources
      .addCase(fetchResources.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchResources.fulfilled, (state, action) => {
        state.resources = action.payload?.resources || action.payload || [];
        state.meta = action.payload?.meta || {};
        state.loading = false;
      })
      .addCase(fetchResources.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Single Resource
      .addCase(fetchResource.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchResource.fulfilled, (state, action) => {
        state.resource = action.payload;
        state.loading = false;
      })
      .addCase(fetchResource.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create Resource
      .addCase(createResource.fulfilled, (state, action) => {
        state.resources.push(action.payload);
      })

      // Update Resource
      .addCase(updateResource.fulfilled, (state, action) => {
        const index = state.resources.findIndex((item) => item.id === action.payload.id);
        if (index !== -1) {
          state.resources[index] = action.payload;
        }
      })

      // Delete Resource
      .addCase(deleteResource.fulfilled, (state, action) => {
        state.resources = state.resources.filter((item) => item.id !== action.payload);
      })

      // Upload Files
      .addCase(uploadFiles.fulfilled, (state, action) => {
        const { resourceId, files } = action.payload;
        const resource = state.resources.find((r) => r.id === resourceId);
        if (resource) {
          resource.files = files;
        }
      })

      // Remove File
      .addCase(removeFile.fulfilled, (state, action) => {
        const { resourceId, fileId } = action.payload;
        const resource = state.resources.find((r) => r.id === resourceId);
        if (resource) {
          resource.files = resource.files.filter((file) => file.id !== fileId);
        }
      });
  },
});

export default resourcesSlice.reducer;